import React, { useContext, useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import { LoadingView, NotificationContext } from "@solsys/solsys-reactjs";
import { useAuth } from "../../contexts/AuthContext";
import { getDefaultRoute, isAdministrator } from "../../utils/roles";

function CopyButton({ text, label }) {
  const { addNotification } = useContext(NotificationContext);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const area = document.createElement("textarea");
        area.value = text;
        document.body.appendChild(area);
        area.select();
        document.execCommand("copy");
        document.body.removeChild(area);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch (err) {
      addNotification("bad", err.message || "Could not copy");
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      style={{
        marginTop: 8,
        padding: "6px 10px",
        borderRadius: 6,
        border: "1px solid var(--card-border)",
        background: "var(--card-bg)",
        color: "var(--text-color)",
        cursor: "pointer",
      }}
    >
      {copied ? "Copied" : label || "Copy"}
    </button>
  );
}

function CodeBlock({ value }) {
  return (
    <pre
      style={{
        margin: "10px 0 0",
        padding: "12px 14px",
        overflowX: "auto",
        borderRadius: 8,
        background: "var(--app-bg)",
        border: "1px solid var(--card-border)",
        fontSize: 13,
        lineHeight: 1.45,
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
      }}
    >
      {value}
    </pre>
  );
}

function CopyField({ label, value, copyLabel }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
      <CodeBlock value={value} />
      <CopyButton text={value} label={copyLabel} />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section
      style={{
        marginBottom: 24,
        padding: "16px 18px",
        borderRadius: 8,
        border: "1px solid var(--card-border)",
        background: "var(--card-bg)",
      }}
    >
      <h3 style={{ marginTop: 0, marginBottom: 8 }}>{title}</h3>
      {children}
    </section>
  );
}

function clientByProvider(clients, provider) {
  return (clients || []).find((client) => client.provider === provider) || null;
}

const API_URL = process.env.REACT_APP_API_URL || "RUNTIME_REPLACE_REACT_APP_API_URL";

export default function McpSettings() {
  const { user } = useAuth();
  const { addNotification } = useContext(NotificationContext);
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState(null);

  useEffect(() => {
    if (!isAdministrator(user)) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/mcp-setup`, { credentials: "include" })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(body.error || "Failed to load MCP setup");
        }
        return body;
      })
      .then((body) => {
        if (!cancelled) setSetup(body);
      })
      .catch((err) => {
        if (!cancelled) addNotification("bad", err.message || "Failed to load MCP setup");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, addNotification]);

  const claude = useMemo(() => clientByProvider(setup?.clients, "claude"), [setup]);
  const lmStudio = useMemo(() => clientByProvider(setup?.clients, "lmstudio"), [setup]);
  const cursor = useMemo(() => clientByProvider(setup?.clients, "cursor"), [setup]);

  if (!isAdministrator(user)) {
    return <Redirect to={getDefaultRoute(user)} />;
  }

  if (loading) return <LoadingView />;

  const mcpUrl = setup?.mcp_url || "";
  const claudeClientId = claude?.client_id || "ecrane-claude";
  const lmStudioClientId = lmStudio?.client_id || "ecrane-lmstudio";
  const cursorClientId = cursor?.client_id || "ecrane-cursor";
  const cursorConfig = JSON.stringify(
    {
      mcpServers: {
        ecrane: {
          url: mcpUrl,
          auth: {
            CLIENT_ID: cursorClientId,
          },
        },
      },
    },
    null,
    2
  );
  const lmStudioConfig = JSON.stringify(
    {
      mcpServers: {
        ecrane: {
          url: mcpUrl,
          auth: {
            CLIENT_ID: lmStudioClientId,
          },
        },
      },
    },
    null,
    2
  );

  return (
    <div style={{ padding: 24, maxWidth: 760 }}>
      <h2 style={{ marginTop: 0 }}>AI connectors (MCP)</h2>
      <p style={{ color: "var(--text-muted, #666)", lineHeight: 1.5 }}>
        Connect Claude, ChatGPT, LM Studio, or Cursor with your E-Crane administrator login. Access is
        read-only. Ticket, crane, and prospect comment text stays hidden unless you allow it on
        the options page after you sign in. After you add the connector, the provider opens a
        browser window so you can sign in.
      </p>

      <Section title="Claude">
        <p style={{ marginTop: 0, lineHeight: 1.5 }}>
          Claude cannot add a custom connector from a chat prompt. You add it in Settings, then
          click Connect to sign in.
        </p>
        <ol style={{ margin: "0 0 8px", paddingLeft: "1.2em", lineHeight: 1.7 }}>
          <li>
            In Claude, open <strong>Customize → Connectors</strong> (or{" "}
            <strong>Organization settings → Connectors</strong> if you are a Team/Enterprise
            owner adding it for the org).
          </li>
          <li>
            Click <strong>+</strong> then <strong>Add custom connector</strong>. Team/Enterprise
            owners choose <strong>Add → Custom → Web</strong>.
          </li>
          <li>Paste the MCP URL below as the remote server URL.</li>
          <li>
            Open <strong>Advanced settings</strong>. Set OAuth Client ID to the value below.
            Leave the client secret blank.
          </li>
          <li>
            Click <strong>Add</strong>, then <strong>Connect</strong> and sign in with your
            E-Crane administrator account.
          </li>
          <li>
            In a chat, use <strong>+</strong> → <strong>Connectors</strong> if you need to enable
            it for that conversation.
          </li>
        </ol>
        <CopyField label="MCP URL" value={mcpUrl} copyLabel="Copy Claude MCP URL" />
        <CopyField
          label="OAuth Client ID"
          value={claudeClientId}
          copyLabel="Copy Claude client ID"
        />
      </Section>

      <Section title="ChatGPT">
        <p style={{ marginTop: 0, lineHeight: 1.5 }}>
          ChatGPT only accepts a remote HTTPS MCP server. On Plus, Pro, Business, Enterprise, and
          Education (web), add it as an <strong>MCP server</strong>. ChatGPT does not ask for an
          OAuth client ID. The header key / value fields are optional static HTTP headers — leave
          them empty. After you save the server, ChatGPT discovers OAuth and opens an E-Crane
          sign-in page.
        </p>
        <ol style={{ margin: "0 0 8px", paddingLeft: "1.2em", lineHeight: 1.7 }}>
          <li>
            If you have a Developer mode toggle, turn it on under{" "}
            <strong>Settings → Apps &amp; Connectors → Advanced settings</strong> (or Settings →
            Security and login). Workspace admins may need to enable this first.
          </li>
          <li>
            Choose <strong>Add MCP server</strong> (or Settings → Apps &amp; Connectors → Create).
          </li>
          <li>
            Name it <strong>E-Crane</strong> and paste the MCP URL below as the server URL.
          </li>
          <li>
            Leave every <strong>header</strong> key and value blank. Do not put a client ID,
            username, or password there — those headers are only for static API keys, which
            E-Crane does not use.
          </li>
          <li>
            Save. When ChatGPT opens the browser sign-in, use your E-Crane administrator account.
          </li>
          <li>
            Start a new chat and enable the server if ChatGPT asks you to pick tools or Developer
            mode.
          </li>
        </ol>
        <CopyField label="MCP URL" value={mcpUrl} copyLabel="Copy ChatGPT MCP URL" />
      </Section>

      <Section title="LM Studio">
        <p style={{ marginTop: 0, lineHeight: 1.5 }}>
          LM Studio stores connectors in <code>mcp.json</code>. The editor is behind the Program
          tab, not a top-level Integrations page.
        </p>
        <ol style={{ margin: "0 0 8px", paddingLeft: "1.2em", lineHeight: 1.7 }}>
          <li>Open LM Studio (0.3.17 or later).</li>
          <li>
            In the <strong>right sidebar</strong>, switch to the <strong>Program</strong> tab
            (terminal icon).
          </li>
          <li>
            Click <strong>Install</strong>, then <strong>Edit mcp.json</strong>. That opens the
            in-app editor. The file is also at <code>~/.lmstudio/mcp.json</code> on Mac/Linux or{" "}
            <code>%USERPROFILE%\.lmstudio\mcp.json</code> on Windows.
          </li>
          <li>
            Merge the snippet below into <code>mcpServers</code> (or replace the file if it is
            empty). Save. LM Studio reloads on save.
          </li>
          <li>
            Approve the browser window it opens and sign in with your E-Crane administrator
            account.
          </li>
        </ol>
        <CopyField
          label="mcp.json snippet"
          value={lmStudioConfig}
          copyLabel="Copy LM Studio config"
        />
      </Section>

      <Section title="Cursor">
        <p style={{ marginTop: 0, lineHeight: 1.5 }}>
          Cursor stores remote MCP servers in <code>mcp.json</code>. Use a static client id and
          leave the secret empty. Cursor sends its own redirect URI. Desktop uses{" "}
          <code>http://localhost:8787/callback</code>.
        </p>
        <ol style={{ margin: "0 0 8px", paddingLeft: "1.2em", lineHeight: 1.7 }}>
          <li>
            Open <strong>Cursor Settings → MCP</strong>.
          </li>
          <li>
            Edit <code>~/.cursor/mcp.json</code> for every project, or{" "}
            <code>.cursor/mcp.json</code> in one project.
          </li>
          <li>
            Merge the snippet below into <code>mcpServers</code>. The URL is this environment,
            including production when you are signed in there.
          </li>
          <li>
            Save. When Cursor opens the browser sign-in, use your E-Crane administrator account.
          </li>
          <li>Do not add a client secret. The client id is already in the snippet.</li>
        </ol>
        <CopyField
          label="mcp.json snippet"
          value={cursorConfig}
          copyLabel="Copy Cursor config"
        />
      </Section>
    </div>
  );
}
